import { canReadProperties, coreQueryKey } from '@/api/core/authorization';
import { purposes } from '@/api/core/native-presentation';
import {
  readPublicMidtermProperty,
  readPublicMidtermSales,
  readRoomMidtermQuote,
  type PublicImageGroup,
  type PublicPropertyPresentation,
  type PublicSalesImage,
  type PublicSummary,
  type PublicText,
  type PublicTypePresentation,
  type PublicUnitFloorPlans,
} from '@/api/core/native-public-sales';
import { safeImageUri } from '@/lib/safe-image-uri';
import { Button, DatePicker } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { languageLabel, purposeLabel } from './media/media-presentation';
import type { PropertyRuntime } from './property-queries';

export const dateValue = (value: Date | undefined) =>
  value
    ? `${String(value.getFullYear())}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
    : '';
export const dateObject = (value: string) => (value ? new Date(`${value}T12:00:00`) : undefined);

export function NativePublicSalesSection({
  readEnabled = true,
  runtime,
  generation,
  propertyId,
  typeId,
  roomId,
  unitId,
}: {
  readEnabled?: boolean;
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  typeId?: string | null;
  roomId?: string | null;
  unitId?: string | null;
}) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [period, setPeriod] = useState<{ first: string; last: string } | null>(null);
  const state = runtime.session.getSnapshot();
  const allowed =
    state.generation === generation &&
    state.status === 'authorized' &&
    state.authorization !== null &&
    !state.deniedDomains.includes('property') &&
    canReadProperties(state.authorization);
  const list = useQuery({
    queryKey: [...coreQueryKey(runtime.apiNamespace, generation, 'public-midterm-list'), period],
    queryFn: ({ signal }) =>
      period
        ? readPublicMidtermSales(runtime.client, period.first, period.last, signal)
        : Promise.reject(new Error('기간을 선택하세요.')),
    enabled: readEnabled && allowed && period !== null,
    retry: false,
    staleTime: 0,
    meta: { suppressGlobalError: true },
  });
  const detail = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'public-midterm-property', propertyId),
      period,
    ],
    queryFn: ({ signal }) =>
      period
        ? readPublicMidtermProperty(runtime.client, propertyId, period.first, period.last, signal)
        : Promise.reject(new Error('기간을 선택하세요.')),
    enabled:
      readEnabled &&
      allowed &&
      period !== null &&
      list.data?.some((item) => item.property_id === propertyId) === true,
    retry: false,
    staleTime: 0,
    meta: { suppressGlobalError: true },
  });
  const selectedPublicType = detail.data?.property.room_types?.find(
    (type) => type.room_type_id === typeId,
  );
  const visibleRooms =
    detail.data?.rooms.filter((room) => !roomId || room.room_id === roomId) ?? [];
  if (!allowed) return null;
  return (
    <section className="body-sm space-y-4 rounded-md border p-4 leading-relaxed">
      <h2 className="font-semibold">고객 공개 결과 확인</h2>
      <p className="body-sm text-muted-foreground">
        선택한 전체 기간의 목록·상세·객실 견적을 확인하세요. 조회 결과는 예약이나 결제를 보장하지
        않습니다.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="body-sm grid gap-1">
          첫 숙박일
          <DatePicker
            value={dateObject(first)}
            onChange={(date) => {
              setFirst(dateValue(date));
            }}
          />
        </div>
        <div className="body-sm grid gap-1">
          마지막 포함일
          <DatePicker
            value={dateObject(last)}
            onChange={(date) => {
              setLast(dateValue(date));
            }}
          />
        </div>
        <Button
          className="h-auto min-h-8 whitespace-normal"
          disabled={!first || !last || first > last}
          onClick={() => {
            if (period?.first === first && period.last === last) {
              void list.refetch();
              void detail.refetch();
            } else setPeriod({ first, last });
          }}
        >
          고객 결과 조회
        </Button>
      </div>
      {period && (
        <div className="body-sm mt-3 space-y-2" role="status">
          {list.isError ? (
            <p>목록을 확인할 수 없습니다. 현재 공개 상태와 기간을 확인한 뒤 다시 조회하세요.</p>
          ) : list.isPending ? (
            <p>목록 조회 중…</p>
          ) : (
            <p>
              목록:{' '}
              {list.data.some((item) => item.property_id === propertyId)
                ? '이 지점 표시됨'
                : '이 지점 표시되지 않음'}
            </p>
          )}
          {detail.isError ? (
            <p>상세와 견적을 확인할 수 없습니다. 서버 설정을 확인하세요.</p>
          ) : detail.data &&
            !list.isFetching &&
            list.data?.some((item) => item.property_id === propertyId) ? (
            <>
              <p>
                상세: {detail.data.property.property_name} ·{' '}
                {detail.data.property.sold_out
                  ? '선택 기간 매진'
                  : `이용 가능한 객실 ${String(detail.data.property.available_room_count)}개`}
              </p>
              <section aria-label="고객 공개 사진" className="space-y-3">
                <h3 className="font-medium">현재 서버의 공개 사진 · 요청 기간 기준</h3>
                <p>
                  저장된 선택과 별도인 공개 조회 결과입니다. 사진 언어를 혼합하거나 자동 대체하지
                  않습니다.
                </p>
                {typeId ? (
                  detail.data.property.room_types
                    ?.filter((type) => type.room_type_id === typeId)
                    .map((type) => (
                      <div key={type.room_type_id}>
                        <h4>{type.room_type_name}</h4>
                        {type.room_type_images.map((group, index) => (
                          <div key={index}>
                            <p>
                              {purposeLabel(group.purpose)} · {languageLabel(group.language)} ·{' '}
                              {group.category ?? '분류 미설정'}
                            </p>
                            <PublicPhotoList photos={group.images} />
                          </div>
                        ))}
                        {!type.room_type_images.length && (
                          <p>이 유형에 공개된 사진 분류가 없습니다.</p>
                        )}
                      </div>
                    ))
                ) : (
                  <PropertyPlacements property={detail.data.property} />
                )}
                {typeId && !detail.data.property.room_types && (
                  <p>현재 서버 응답에는 유형별 공개 사진 정보가 없습니다.</p>
                )}
                {typeId && detail.data.property.room_types && !selectedPublicType && (
                  <p>선택한 객실 유형이 이 공개 결과에 없습니다.</p>
                )}
              </section>
              <PublicPropertyContent presentation={detail.data.property.presentation} />
              {typeId && selectedPublicType && (
                <PublicTypeContent presentation={selectedPublicType.presentation} />
              )}
              {visibleRooms.length ? (
                <ul className="list-disc pl-5">
                  {visibleRooms.map((room) => (
                    <li key={room.room_id}>
                      객실 상세 숙박료: {room.daily_price_minor.toLocaleString()}원/일 · 숙박료 합계{' '}
                      {room.total_amount_minor.toLocaleString()}원 · {room.first_date}~
                      {room.last_date}
                      <RoomQuote
                        key={`${room.room_id}-${unitId ?? 'all'}-${period.first}-${period.last}`}
                        runtime={runtime}
                        generation={generation}
                        roomId={room.room_id}
                        unitId={unitId}
                        first={period.first}
                        last={period.last}
                        offerDaily={room.daily_price_minor}
                        offerAccommodation={room.total_amount_minor}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <p>선택 기간의 객실 견적 없음</p>
              )}
            </>
          ) : null}
        </div>
      )}
    </section>
  );
}

function RoomQuote({
  runtime,
  generation,
  roomId,
  unitId,
  first,
  last,
  offerDaily,
  offerAccommodation,
}: {
  runtime: PropertyRuntime;
  generation: number;
  roomId: string;
  unitId?: string | null;
  first: string;
  last: string;
  offerDaily: number;
  offerAccommodation: number;
}) {
  const [result, setResult] = useState('');
  const [floorPlans, setFloorPlans] = useState<{
    all: PublicImageGroup[] | undefined;
    units: PublicUnitFloorPlans[] | undefined;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const state = runtime.session.getSnapshot();
  const allowed =
    state.generation === generation &&
    state.status === 'authorized' &&
    state.authorization !== null &&
    !state.deniedDomains.includes('property') &&
    canReadProperties(state.authorization);
  const read = async () => {
    const latest = runtime.session.getSnapshot();
    if (
      busy ||
      latest.generation !== generation ||
      latest.status !== 'authorized' ||
      !latest.authorization ||
      latest.deniedDomains.includes('property') ||
      !canReadProperties(latest.authorization)
    )
      return;
    setBusy(true);
    setResult('');
    setFloorPlans(null);
    try {
      const quote = await readRoomMidtermQuote(runtime.client, roomId, first, last);
      const after = runtime.session.getSnapshot();
      if (
        after.generation === generation &&
        after.status === 'authorized' &&
        !after.deniedDomains.includes('property') &&
        after.authorization &&
        canReadProperties(after.authorization)
      ) {
        setFloorPlans({ all: quote.floorPlans, units: quote.unitFloorPlans });
        setResult(
          `고객 현재 견적: ${quote.daily.toLocaleString()}원/일 · 숙박료 ${quote.accommodation.toLocaleString()}원 · 보증금 ${quote.deposit.toLocaleString()}원 · 청소비 ${quote.cleaning.toLocaleString()}원 · 결제 예정 합계 ${quote.payable.toLocaleString()}원. 상세와 ${quote.daily === offerDaily && quote.accommodation === offerAccommodation ? '숙박료 일치' : '숙박료 차이: 최신 공개 상태를 재조회하세요'}.`,
        );
      }
    } catch {
      if (allowed && runtime.session.getSnapshot().generation === generation)
        setResult(
          '현재 견적을 확인할 수 없습니다. 입퇴실 시각과 달력 등 서버 준비 상태를 확인하세요.',
        );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 space-y-3">
      <Button
        className="h-auto min-h-8 whitespace-normal"
        variant="outline"
        disabled={!allowed || busy}
        onClick={() => void read()}
      >
        현재 객실 견적 확인
      </Button>
      {allowed && result && <p role="status">{result}</p>}
      {allowed &&
        floorPlans &&
        (unitId ? (
          <section aria-label="선택 슬롯 평면도" className="mt-3 space-y-2">
            <h4 className="font-medium">호실 선택 · 선택 슬롯의 공개 평면도</h4>
            {floorPlans.units === undefined ? (
              <p>현재 서버는 선택 슬롯 귀속 정보를 지원하지 않습니다.</p>
            ) : (
              <PublicImageGroups
                groups={
                  floorPlans.units.find((unit) => unit.unit_id === unitId)?.image_groups ?? []
                }
              />
            )}
          </section>
        ) : (
          <section aria-label="선택 객실의 슬롯 평면도" className="mt-3 space-y-2">
            <h4 className="font-medium">호실 선택 · 선택 객실의 슬롯 평면도</h4>
            {floorPlans.all === undefined ? (
              <p>현재 서버 응답에는 객실 평면도 정보가 없습니다.</p>
            ) : (
              <PublicImageGroups groups={floorPlans.all} />
            )}
          </section>
        ))}
    </div>
  );
}

const publicTextLabels: Partial<Record<string, string>> = purposes;
const publicValue = (value: string | null) =>
  value === null ? '미설정' : value === '' ? '(빈 문구)' : value;
export function PublicTexts({ values }: { values: PublicText[] | null }) {
  if (values === null) return <p>현재 공개 문구가 설정되지 않았습니다.</p>;
  if (!values.length) return <p>공개 조회에서 반환된 문구 없음</p>;
  return (
    <dl className="space-y-2">
      {values.map((text, index) => (
        <div key={index}>
          <dt className="text-muted-foreground">
            {publicTextLabels[text.purpose] ?? text.purpose} · {languageLabel(text.language)}
          </dt>
          <dd className="whitespace-pre-wrap">{publicValue(text.value)}</dd>
        </div>
      ))}
    </dl>
  );
}
export function PublicImageGroups({ groups }: { groups: PublicImageGroup[] }) {
  if (!groups.length) return <p>공개 조회에서 반환된 사진 분류 없음</p>;
  return (
    <>
      {groups.map((group, index) => (
        <div key={index}>
          <p>
            {purposeLabel(group.purpose)} · {languageLabel(group.language)} ·{' '}
            {group.category ?? '분류 미설정'}
          </p>
          <PublicPhotoList photos={group.images} />
        </div>
      ))}
    </>
  );
}
function PlacementPhotos({ photos }: { photos: PublicSalesImage[] | undefined }) {
  const first = photos?.[0];
  if (!first) return <PublicPhotoList photos={photos} />;
  return (
    <>
      <PublicPhoto photo={first} index={0} />
      {photos.length > 1 && (
        <details>
          <summary className="cursor-pointer">
            이어지는 사진 {photos.length - 1}장 · 서버 순서
          </summary>
          <PublicPhotoList photos={photos.slice(1)} />
        </details>
      )}
    </>
  );
}
export function PropertyPlacements({ property }: { property: PublicSummary }) {
  const intro = property.presentation?.texts?.filter((text) =>
    ['DISPLAY_NAME', 'TAGLINE', 'DESCRIPTION'].includes(text.purpose),
  );
  const basis = property.display_price_basis;
  return (
    <div className="grid items-start gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
      <section
        aria-label="검색 결과 카드 미리보기"
        className="bg-background overflow-hidden rounded-lg border"
      >
        <p className="border-b p-3 font-medium">검색 결과 · 지점 카드</p>
        <PlacementPhotos photos={property.card_images} />
        <div className="space-y-2 p-3">
          <h4 className="font-semibold">{property.property_name}</h4>
          {typeof property.minimum_daily_price_minor === 'number' && (
            <p>
              최저 일 숙박료 {property.minimum_daily_price_minor.toLocaleString()}원
              <span className="text-muted-foreground block">
                표시 기준:{' '}
                {basis === 'requested'
                  ? '요청 기간'
                  : basis === 'suggested'
                    ? '제안 기간'
                    : basis === 'catalog_min'
                      ? '요청 기간 가정 참고가'
                      : (basis ?? '미제공')}
              </span>
            </p>
          )}
          <p>
            {property.sold_out
              ? '선택 기간 매진'
              : `이용 가능한 객실 ${String(property.available_room_count)}개`}
          </p>
        </div>
      </section>
      <section
        aria-label="지점 상세 상단 미리보기"
        className="bg-background overflow-hidden rounded-lg border"
      >
        <p className="border-b p-3 font-medium">지점 상세 · 상단 대표 사진과 소개</p>
        <PlacementPhotos photos={property.hero_images} />
        <div className="space-y-3 p-4">
          <h4 className="font-semibold">{property.property_name}</h4>
          {intro ? <PublicTexts values={intro} /> : <p>현재 공개 소개 정보 없음</p>}
        </div>
      </section>
    </div>
  );
}

export function PublicPropertyContent({
  presentation,
}: {
  presentation: PublicPropertyPresentation | null | undefined;
}) {
  return (
    <section aria-label="지점 상세 공개 내용" className="space-y-3 rounded-md border p-3">
      <h3 className="font-medium">지점 상세 · 소개 / 공용공간 / 위치 / FAQ</h3>
      {presentation === undefined ? (
        <p>현재 서버 응답에는 지점 공개 내용 정보가 없습니다.</p>
      ) : presentation === null ? (
        <p>현재 지점의 공개 내용이 설정되지 않았습니다.</p>
      ) : (
        <>
          <details>
            <summary className="cursor-pointer">지점 문구 전체 · 언어별 원문 확인</summary>
            <PublicTexts values={presentation.texts} />
          </details>
          {presentation.components === null ? (
            <p>공개 공간·위치 안내가 설정되지 않았습니다.</p>
          ) : presentation.components.length === 0 ? (
            <p>공개 공간·위치 안내 없음</p>
          ) : (
            presentation.components.map((component, index) => (
              <section key={index} className="space-y-2 rounded-md border p-3">
                <h4>
                  {(
                    {
                      COMMON_AREA: '공용공간',
                      LOCATION_BULLET: '위치 안내',
                      LOCATION_LINE: '교통 안내',
                      FLOOR_DIRECTORY: '층 안내',
                    } as Record<string, string>
                  )[component.kind] ?? component.kind}
                </h4>
                {component.parent_index != null && (
                  <div className="border-l-2 pl-3">
                    <p className="text-muted-foreground">상위 위치 안내</p>
                    <PublicTexts
                      values={presentation.components?.[component.parent_index]?.texts ?? null}
                    />
                  </div>
                )}
                {component.line_code != null && <p>노선: {publicValue(component.line_code)}</p>}
                <PublicTexts values={component.texts} />
                <PublicImageGroups groups={component.images} />
              </section>
            ))
          )}
          <h4 className="font-medium">상세 하단 · FAQ</h4>
          {presentation.faqs.length === 0 ? (
            <p>상세에 공개된 FAQ 없음</p>
          ) : (
            presentation.faqs.map((faq) => (
              <div key={faq.id} className="rounded-md border p-3">
                {(['ko', 'en'] as const).map((language) => (
                  <div key={language} className="whitespace-pre-wrap">
                    <p className="text-muted-foreground">{languageLabel(language)}</p>
                    <p>{publicValue(faq.question[language])}</p>
                    <p>{publicValue(faq.answer[language])}</p>
                  </div>
                ))}
              </div>
            ))
          )}
        </>
      )}
    </section>
  );
}
export function PublicTypeContent({
  presentation,
}: {
  presentation: PublicTypePresentation | null | undefined;
}) {
  return (
    <section aria-label="객실 유형 공개 내용" className="space-y-2 rounded-md border p-3">
      <h3 className="font-medium">객실 유형 선택 · 소개 / 가구 / 시설</h3>
      {presentation === undefined ? (
        <p>현재 서버 응답에는 유형 공개 내용 정보가 없습니다.</p>
      ) : presentation === null ? (
        <p>현재 유형의 공개 내용이 설정되지 않았습니다.</p>
      ) : (
        <>
          <PublicTexts values={presentation.texts} />
          {(['furniture', 'facilities'] as const).map((key) => (
            <div key={key}>
              <h4>{key === 'furniture' ? '가구' : '시설'}</h4>
              {presentation[key] === null ? (
                <p>미설정</p>
              ) : presentation[key].length === 0 ? (
                <p>공개 항목 없음</p>
              ) : (
                <ul className="list-disc pl-5">
                  {presentation[key].map((value, index) => (
                    <li key={index}>{publicValue(value)}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </>
      )}
    </section>
  );
}

function PublicPhoto({ photo, index }: { photo: PublicSalesImage; index: number }) {
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const src = safeImageUri(photo.thumbnail_url) ?? safeImageUri(photo.url);
  if (!src) return <p>현재 사진 주소를 표시할 수 없습니다.</p>;
  if (failed)
    return (
      <div role="status">
        <p>공개 사진을 불러오지 못했습니다.</p>
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          onClick={() => {
            setFailed(false);
            setRetry((n) => n + 1);
          }}
        >
          공개 사진 다시 불러오기
        </Button>
      </div>
    );
  return (
    <img
      key={retry}
      src={src}
      alt={`공개 사진 ${String(index + 1)}`}
      className="aspect-video w-full rounded-md object-contain"
      loading="lazy"
      decoding="async"
      onError={() => {
        setFailed(true);
      }}
    />
  );
}
export function PublicPhotoList({ photos }: { photos: PublicSalesImage[] | undefined }) {
  if (!photos) return <p>현재 서버 응답에는 이 공개 사진 정보가 없습니다.</p>;
  if (!photos.length) return <p>공개 조회에서 반환된 사진 없음</p>;
  return (
    <ul className="grid gap-3 sm:grid-cols-3">
      {photos.map((photo, index) => (
        <li key={`${photo.url}:${String(index)}`}>
          <PublicPhoto photo={photo} index={index} />
        </li>
      ))}
    </ul>
  );
}
